
import { useState } from "react";
import Image from "./Image";
import { ChevronDown } from "lucide-react";
const AUDIENCES = [
    {
        label: "Independent Rental Operators",
        tooltip: "Replace disconnected tools, paperwork, and manual follow up with one connected rental operation. Manage bookings, payments, agreements, embedded insurance, driver verification, active license checks, fleet activity, violations, and claims without adding more work to your day.",
    },
    {
        label: "New Rental Businesses",
        tooltip: "Get started for free, build your rental business quickly, and begin renting the same day. Your storefront, bookings, payments, agreements, verification, and insurance workflows are already built in, so you can launch on your own time and grow without changing platforms later.",
    },
    {
        label: "Marketplace Hosts & Digital Rental",
        tooltip: "Take control of your rental business with your brand front and center. Accept private and direct bookings with embedded insurance, built in verification, payments, collections, and resolution tools. Bring in leads from OTAs, local channels, and online marketing while deciding which renters are right for your business.",
    },
    {
        label: "Physical Storefront Rental Agencies",
        tooltip: "Let renters complete verification, agreements, payments, and check in before they arrive. Your team can manage trip changes, send messages, collect balances, and access documents without printing paperwork, repeating information, or creating another line at the counter.",
    },
    {
        label: "Rideshare and Gig Rental Fleets",
        tooltip: "Give qualified drivers faster access to your vehicles with built in identity, license, DMV, insurance, and rideshare platform verification. Collect weekly or monthly payments, issue insurance cards in the driver's name, keep every driver's information in one account, communicate in real time, and provide integrated roadside assistance to keep them moving.",
    },
    {
        label: "Franchise and Multi-Location Operators",
        tooltip: "Give customers the fast, contactless experience they now expect across every location. Complete security checks before the trip, keep renters connected through live messaging during the rental, and resolve balances, violations, and claims faster afterward. Manage fleets, teams, and reporting from one place.",
    },
];
/* Hover-to-open is a desktop affordance only; below the sm breakpoint rows open on tap,
   even when a mouse is driving a narrow window. */
const hoverOpens = () => window.matchMedia("(min-width: 640px)").matches;
function AudienceList() {
    const [openIndex, setOpenIndex] = useState(null);
    return (<ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 lg:gap-x-14 items-start" onPointerLeave={(e) => {
            if (e.pointerType === "mouse" && hoverOpens())
                setOpenIndex(null);
        }}>
      {AUDIENCES.map((item, i) => {
            const open = openIndex === i;
            return (<li key={item.label} className={[
                    "border-b border-border",
                    i === 0 ? "border-t" : "",
                    i === 1 ? "sm:border-t" : "",
                ].join(" ")}>
            <button type="button" onPointerEnter={(e) => {
                    if (e.pointerType === "mouse" && hoverOpens())
                        setOpenIndex(i);
                }} onClick={() => setOpenIndex((prev) => (prev === i ? null : i))} className="group w-full text-left py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30 rounded-sm" aria-expanded={open}>
              <span className="flex items-center gap-3">
                {/* Growing accent line (desktop only; mobile uses the chevron) */}
                <span className={[
                    "h-px shrink-0 transition-all duration-300 max-sm:hidden",
                    open ? "w-10 bg-foreground" : "w-5 bg-border group-hover:w-8 group-hover:bg-foreground/60",
                ].join(" ")} aria-hidden="true"/>
                <span className={[
                    "flex-1 text-base sm:text-lg md:text-xl font-semibold tracking-tight transition-colors duration-200",
                    open ? "text-foreground" : "text-muted-foreground group-hover:text-foreground",
                ].join(" ")}>
                  {item.label}
                </span>
                <ChevronDown aria-hidden="true" className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-300 sm:hidden ${open ? "rotate-180 text-foreground" : ""}`}/>
              </span>

              {/* Reveal description */}
              <span className={[
                    "grid transition-all duration-300 ease-out",
                    open ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0",
                ].join(" ")}>
                <span className="overflow-hidden">
                  <span className="block pr-2 text-sm sm:pl-8 sm:text-base text-muted-foreground leading-relaxed text-pretty">
                    {item.tooltip}
                  </span>
                </span>
              </span>
            </button>
          </li>);
        })}
    </ul>);
}
export function BusinessSizesSection() {
    return (<section>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-16 py-12 md:py-20">

        {/* Eyebrow above the title */}
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
          Modern Mobility Operators
        </p>

        {/* Two-column header — heading left, subcopy right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-start mb-12 md:mb-16">
          <h2 className="text-[28px] sm:text-3xl md:text-[40px] font-bold tracking-tighter leading-[1.1] text-balance text-foreground">
            Powering rental commerce for businesses of all sizes and from all segments.
          </h2>
          <p className="text-2xl text-muted-foreground leading-relaxed text-pretty lg:pt-2">
            Run your rental fleet on a reliable, automated platform built for one or thousands of vehicles.
          </p>
        </div>

        {/* Audience reveal list — 2 columns of 3 */}
        <div>
          <AudienceList />
        </div>

        {/* Aerial intersection image */}
        <div className="mt-12 md:mt-16">
          <Image src="/images/inshur/intersection-aerial.webp" alt="Aerial view of a busy city intersection with vehicles crossing" width={1920} height={840} className="w-full rounded-2xl object-cover"/>
        </div>

      </div>
    </section>);
}
